import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LockKeyhole, LogIn, ShieldCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import {
  WebAuthFailureCodeSchema,
  WebAuthSessionSchema,
  type WebAuthFailureCode,
  type WebAuthSession,
  type WebAuthRole,
  type WebAuthUser,
} from '@tcode/contracts';

import { apiRequest } from '../../api/client.js';
import { ErrorState, LoadingState } from '../../components/PageState.js';
import { PreferenceControls } from '../../components/PreferenceControls.js';
import { useUiPreferences } from '../../i18n/ui-preferences.js';

const AUTH_QUERY_KEY = ['web-auth-session'] as const;

interface WebAuthContextValue {
  provider: 'local' | 'feishu';
  role: WebAuthRole;
  user: WebAuthUser | null;
  logout(): Promise<void>;
}

const WebAuthContext = createContext<WebAuthContextValue | null>(null);

export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { t } = useUiPreferences();
  const session = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => WebAuthSessionSchema.parse(await apiRequest('/api/v1/auth/session')),
    retry: false,
    staleTime: 60_000,
  });

  if (session.isPending) {
    return <LoadingState label={t('正在验证公司身份', 'Verifying company identity')} />;
  }
  if (session.isError) {
    return <ErrorState error={session.error} retry={() => void session.refetch()} />;
  }
  if (!session.data.authenticated) return <LoginPage session={session.data} />;

  return (
    <WebAuthContext.Provider
      value={{
        provider: session.data.provider,
        role: session.data.role,
        user: session.data.user,
        logout: async () => {
          await apiRequest('/api/v1/auth/session', { method: 'DELETE' });
          await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
        },
      }}
    >
      {children}
    </WebAuthContext.Provider>
  );
}

export function useWebAuth(): WebAuthContextValue {
  const value = useContext(WebAuthContext);
  if (!value) throw new Error('useWebAuth must be used inside AuthGate');
  return value;
}

export function useOptionalWebAuth(): WebAuthContextValue | null {
  return useContext(WebAuthContext);
}

function LoginPage({ session }: { session: Extract<WebAuthSession, { authenticated: false }> }) {
  const { t } = useUiPreferences();
  const location = useLocation();
  const loginLinkRef = useRef<HTMLAnchorElement>(null);
  const loginStartedRef = useRef(false);
  const search = new URLSearchParams(location.search);
  const rawFailureCode = search.get('authError');
  const parsedFailureCode = WebAuthFailureCodeSchema.safeParse(
    rawFailureCode === 'feishu' ? 'provider_error' : rawFailureCode,
  );
  const failureCode = parsedFailureCode.success ? parsedFailureCode.data : null;
  search.delete('authError');
  const query = search.toString();
  const returnTo = `${location.pathname}${query ? `?${query}` : ''}${location.hash}`;
  const authorizeUrl = `/api/v1/auth/feishu?${new URLSearchParams({ returnTo }).toString()}`;
  const autoStartLogin =
    session.provider === 'feishu' &&
    (Boolean(search.get('item')) ||
      Boolean(search.get('feishuProjectUrl')) ||
      location.pathname === '/meego/connect' ||
      location.pathname === '/meego/restart' ||
      location.pathname === '/meego/interrupt') &&
    !failureCode;

  useEffect(() => {
    if (!autoStartLogin || loginStartedRef.current) return;
    loginStartedRef.current = true;
    loginLinkRef.current?.click();
  }, [autoStartLogin]);

  return (
    <main className="login-page">
      <div className="login-preferences">
        <PreferenceControls />
      </div>
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-brand" aria-label="TCode Delivery OS">
          <span className="brand-mark large">T</span>
          <span>
            <strong>TCode</strong>
            <small>Delivery OS</small>
          </span>
        </div>
        <div className="login-security-mark" aria-hidden="true">
          <ShieldCheck />
        </div>
        <h1 id="login-title">{t('使用公司飞书账号登录', 'Sign in with company Feishu')}</h1>
        <p>
          {t(
            'TCode 包含代码、需求和交付记录，仅向公司飞书组织成员开放。',
            'TCode contains source code and delivery records and is available only to company Feishu members.',
          )}
        </p>
        {failureCode ? (
          <div className="login-error" role="alert">
            {loginFailureMessage(failureCode, t)}
          </div>
        ) : null}
        <a ref={loginLinkRef} className="button primary login-action" href={authorizeUrl}>
          <LogIn aria-hidden="true" />
          {t('继续使用飞书登录', 'Continue with Feishu')}
        </a>
        <div className="login-boundary">
          <LockKeyhole aria-hidden="true" />
          <span>
            {session.provider === 'feishu'
              ? t(
                  '身份由企业自建飞书应用验证，TCode 不接收你的飞书密码。',
                  'Identity is verified by the company Feishu app. TCode never receives your password.',
                )
              : null}
          </span>
        </div>
      </section>
    </main>
  );
}

function loginFailureMessage(
  code: WebAuthFailureCode,
  t: (chinese: string, english: string) => string,
): string {
  switch (code) {
    case 'invalid_state':
      return t(
        '登录请求已过期或已使用，请重新发起登录。',
        'This sign-in request expired or was already used. Start sign-in again.',
      );
    case 'tenant_not_allowed':
      return t(
        '当前飞书账号不属于 TCode 允许的企业，请切换公司账号或联系管理员。',
        'This Feishu account is outside the organization allowed by TCode. Switch to your company account or contact an administrator.',
      );
    case 'user_not_allowed':
      return t(
        '当前账号尚未获得 TCode 访问权限，请联系管理员。',
        'This account does not have access to TCode. Contact an administrator.',
      );
    case 'provider_error':
      return t(
        '飞书服务暂时未能完成身份验证，请稍后重试；持续失败请联系管理员。',
        'Feishu could not complete identity verification. Try again later or contact an administrator if it continues.',
      );
  }
}
